import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const pinDiv = (color) => L.divIcon({
  className: "route-pin",
  html: `<div style="background:${color};width:20px;height:20px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:3px solid #090D14;box-shadow:0 0 8px rgba(0,0,0,0.4)"></div>`,
  iconSize: [20, 20], iconAnchor: [10, 20],
});

// Vanilla Leaflet — StrictMode-safe (no double MapContainer init).
export default function RoutePreviewMap({ pickup, dropoff }) {
  const divRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({ pickup: null, dropoff: null, line: null });
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);

  // Mount map once
  useEffect(() => {
    if (!divRef.current || mapRef.current) return;
    const map = L.map(divRef.current, { zoomControl: false, scrollWheelZoom: false }).setView([-36.8485, 174.7633], 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png", { attribution: "&copy; OSM" }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layersRef.current = { pickup: null, dropoff: null, line: null };
    };
  }, []);

  // Update markers + route on prop change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !pickup || !dropoff) return;
    const l = layersRef.current;
    // Remove old
    if (l.pickup) { map.removeLayer(l.pickup); l.pickup = null; }
    if (l.dropoff) { map.removeLayer(l.dropoff); l.dropoff = null; }
    if (l.line) { map.removeLayer(l.line); l.line = null; }
    // Add new markers
    l.pickup = L.marker([pickup.lat, pickup.lng], { icon: pinDiv("#10B981") }).addTo(map);
    l.dropoff = L.marker([dropoff.lat, dropoff.lng], { icon: pinDiv("#EF4444") }).addTo(map);
    map.fitBounds(L.latLngBounds([[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]]), { padding: [24, 24] });

    let cancel = false;
    (async () => {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${pickup.lng},${pickup.lat};${dropoff.lng},${dropoff.lat}?geometries=geojson&overview=full`;
        const r = await fetch(url);
        const d = await r.json();
        const route = d?.routes?.[0];
        if (!route || cancel || !mapRef.current) return;
        const latLngs = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
        l.line = L.polyline(latLngs, { color: "#E5A93C", weight: 4, opacity: 0.9 }).addTo(map);
        map.fitBounds(l.line.getBounds(), { padding: [24, 24] });
        setDistance(Math.round((route.distance / 1000) * 10) / 10);
        setDuration(Math.round(route.duration / 60));
      } catch { /* keep markers only */ }
    })();
    return () => { cancel = true; };
  }, [pickup, dropoff]);

  if (!pickup || !dropoff) return null;
  return (
    <div className="rounded-xl overflow-hidden border border-white/10" data-testid="route-preview-map">
      <div ref={divRef} style={{ height: 220, width: "100%" }} />
      {distance !== null && (
        <div className="bg-black/40 border-t border-white/10 px-3 py-2 text-xs font-mono text-slate-300 flex justify-between">
          <span>Route <span className="text-amber-400">{distance} km</span></span>
          <span>ETA <span className="text-amber-400">~{duration} min</span></span>
        </div>
      )}
    </div>
  );
}
